/**
 * Web Components entry point
 * Lit components are exported from here and bundled by esbuild
 */

// ProposalFilter - Filter proposals by status on weekly index pages
export { ProposalFilter } from './proposal-filter.js';
export type { ProposalStatus, FilterStatus, FilterChangeDetail } from './proposal-filter.js';

// ArchiveCalendar - Home page compact month calendar (nav, swipe, touch popovers)
export { ArchiveCalendar } from './archive-calendar.js';

// ProposalSearch - Full-text/semantic search form and results on /search/
export { ProposalSearch } from './proposal-search.js';
export type { SearchResultItem, SearchResponse, SearchErrorResponse } from './proposal-search.js';

// Release item side-panel / bottom-sheet controller (spec-feature.md §3.4)
export { initializeReleasePanel, openReleasePanel, closeReleasePanel, loadPanelContent } from './release-panel.js';

// Link-copy buttons on release headings/items (spec-feature.md §3.3)
export { initializeCopyLinkButtons, copyLinkAndShowFeedback } from './copy-link.js';

// Table-of-contents filter chips on a release page (spec-feature.md §3.3)
export { initializeReleaseTOC, applyTOCFilter } from './release-toc.js';

// Original/Japanese-translation display toggle on a release page
// (spec-translation.md)
export { initializeReleaseLangToggle, toggleReleaseLang } from './release-lang.js';

// Per-Item original/Japanese-translation toggle buttons on a release page
// (spec-translation.md; task #16)
export { initializeReleaseItemLangToggles, resyncItemLangToggles } from './release-item-lang.js';

// Global "/" keyboard shortcut for jumping to search (nav bar, home hero, or
// <proposal-search> itself on /search/)
export { initializeSearchShortcut, focusSearchInput } from './search-shortcut.js';

// Header card cycling through the author's other projects (lg+ only)
export { initializeAuthorTools, showAuthorTool } from './author-tools.js';

import type { FilterChangeDetail, FilterStatus } from './proposal-filter.js';
import { initializeReleasePanel } from './release-panel.js';
import { initializeCopyLinkButtons } from './copy-link.js';
import { initializeReleaseTOC } from './release-toc.js';
import { initializeReleaseLangToggle } from './release-lang.js';
import { initializeReleaseItemLangToggles } from './release-item-lang.js';
import { initializeSearchShortcut } from './search-shortcut.js';
import { initializeAuthorTools } from './author-tools.js';

/**
 * Valid filter status values.
 */
const VALID_FILTER_STATUSES: ReadonlySet<string> = new Set([
  'all',
  'accepted',
  'declined',
  'likely_accept',
  'likely_decline',
  'discussions',
  'active',
  'hold',
]);

/**
 * Type guard to validate FilterChangeDetail with strict status validation.
 */
function isValidFilterDetail(detail: unknown): detail is FilterChangeDetail {
  if (typeof detail !== 'object' || detail === null) {
    return false;
  }
  const d = detail as Record<string, unknown>;
  return typeof d.status === 'string' && VALID_FILTER_STATUSES.has(d.status);
}

/**
 * Initialize filter functionality on the page.
 * This connects the proposal-filter element to proposal list items.
 * Exported for testing purposes.
 */
export function initializeFilters(): void {
  document.querySelectorAll('proposal-filter').forEach((filter) => {
    filter.addEventListener('filter-change', (event: Event) => {
      const customEvent = event as CustomEvent<FilterChangeDetail>;

      // Guard against malformed event detail
      if (!isValidFilterDetail(customEvent.detail)) {
        console.warn('Invalid filter-change event detail:', customEvent.detail);
        return;
      }

      const status: FilterStatus = customEvent.detail.status;
      const container = filter.closest('.weekly-index');
      if (!container) return;

      const proposals = container.querySelectorAll('article[data-status]');
      proposals.forEach((proposal) => {
        const proposalStatus = proposal.getAttribute('data-status');
        if (status === 'all' || proposalStatus === status) {
          (proposal as HTMLElement).style.display = '';
        } else {
          (proposal as HTMLElement).style.display = 'none';
        }
      });
    });
  });
}

/**
 * CSS class marking a `.release-item` that has an inline annotation popover
 * (`.release-annotation-popover`), rendered by
 * internal/site/templates/release_markdown.go's RenderReleaseSource. On
 * hover-capable devices the popover opens purely via CSS
 * (`:hover`/`:focus-within`, layout.templ); this only handles touch devices,
 * which lack `:hover`, by toggling the popover open/closed on tap
 * (spec-feature.md §3.3 タッチ端末対応).
 */
const ANNOTATED_ITEM_SELECTOR = '.release-item.has-annotation';

/** Matches `(hover: none)` environments (touch devices without a mouse). */
function isTouchOnly(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
}

/**
 * On touch-only devices, tapping an annotated `.release-item` toggles its
 * popover open (`data-open` attribute, styled in layout.templ) instead of
 * relying on `:hover`, which touch devices don't reliably provide. Tapping
 * a link inside the open popover navigates normally; tapping elsewhere
 * inside the item toggles it; tapping outside closes any open popover.
 * Exported for testing purposes.
 */
export function initializeReleaseAnnotationTouch(): void {
  if (!isTouchOnly()) return;

  document.querySelectorAll<HTMLElement>(ANNOTATED_ITEM_SELECTOR).forEach((item) => {
    item.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('.release-annotation-popover a')) {
        // Let links inside an already-open popover navigate normally.
        return;
      }
      const isOpen = item.getAttribute('data-open') === 'true';
      document.querySelectorAll<HTMLElement>(`${ANNOTATED_ITEM_SELECTOR}[data-open="true"]`).forEach((other) => {
        if (other !== item) other.removeAttribute('data-open');
      });
      if (isOpen) {
        item.removeAttribute('data-open');
      } else {
        item.setAttribute('data-open', 'true');
      }
    });
  });

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    if (target.closest(ANNOTATED_ITEM_SELECTOR)) return;
    document.querySelectorAll<HTMLElement>(`${ANNOTATED_ITEM_SELECTOR}[data-open="true"]`).forEach((item) => {
      item.removeAttribute('data-open');
    });
  });
}

// Initialize when DOM is ready
function initializeAll(): void {
  initializeFilters();
  initializeReleasePanel();
  initializeReleaseAnnotationTouch();
  initializeCopyLinkButtons();
  initializeReleaseTOC();
  initializeReleaseLangToggle();
  initializeReleaseItemLangToggles();
  initializeSearchShortcut();
  initializeAuthorTools();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAll);
} else {
  initializeAll();
}
